import React, {
  useMemo,
  useEffect,
  useCallback,
  useState,
  useRef,
}                                from 'react';
import { FormikProps }           from 'formik';
import {
  ISearchFilter,
  ISearchParams,
}                                from '@typings/search';
import { IUsageAuctions }        from '@typings/auction';
import {
  ISearchInvoice,
  TInvoiceValues,
  ISoldLot,
  IInvoiceFee,
  IInvoiceDetails,
}                                from '@typings/invoiceManagement';
import {
  ICreditUnion,
  IInvoicesEmailsPayload,
}                                from '@typings/accountManagement';
import { EAppPage }              from '@enums/common';
import {
  EInvoiceStatus,
  EPaymentStatus,
  EFeeType,
}                                from '@enums/invoiceManagement';
import { EAccountPage }          from '@enums/accountManagement';
import {
  useFetchInvoiceById,
  useFetchInvoices,
  useDownloadPdfInvoice,
  useUpdateInvoice,
}                                from '@modules/invoiceManagement/queries/invoiceManagementQueries';
import { useFetchUsageAuctions } from '@modules/auctions/queries/auctionsQueries';
import {
  parseRecords,
  getFormattedDate,
}                                from '@utils/commonFunctions';
import { useToggle }             from '@utils/useToggle';
import { ROWS_PER_PAGE }         from '@utils/suggestions';
import { NadaUsageModal }        from '@modules/accountManagement/components/NadaUsageModal';
import { ConfirmDialog }         from '@modules/accountManagement/components/ConfirmDialog';
import { InvoiceListPage }       from '../pages/InvoiceListPage';
import { InvoiceDetailsPage }    from '../pages/InvoiceDetailsPage';
import {
  useFetchAccountById,
  useFetchInvoiceEmails,
  useSendInvoiceEmails,
}                                from '../queries/accountQueries';

const getInvoiceStatus = ({
  soldLots    = [],
  invoiceFees = [],
}: TInvoiceValues): EInvoiceStatus => {
  const isPaidInvoice = [
    ...soldLots,
    ...invoiceFees,
  ].every((item: ISoldLot | IInvoiceFee) => item.status !== EPaymentStatus.Unpaid);

  return isPaidInvoice ? EInvoiceStatus.Paid : EInvoiceStatus.Unpaid;
};

interface IProps {
  push           : (path: string) => void;
  replace        : (path: string) => void;
  invoiceId      : string;
  currentAccount : ICreditUnion;
  cuid           : string;
}

export const AccountInvoicesContainer: React.FC<IProps> = ({
  push,
  replace,
  invoiceId,
  currentAccount,
  cuid,
}) => {
  const formikRef = useRef<FormikProps<TInvoiceValues>>(null);

  const [usageAuctions, setUsageAuctions]       = useState<IUsageAuctions[]>([]);
  const [currentPage, setCurrentPage]           = useState<number>(0);
  const [resetFeeType, setResetFeeType]         = useState<EFeeType>(EFeeType.Unknown);
  const [deletedSoldLotId, setDeletedSoldLotId] = useState<string | null>(null);
  const [currentInvoice, setCurrentInvoice]     = useState<IInvoiceDetails | null>(null);

  const [isNadaModalOpen, toggleIsNadaModalOpen]         = useToggle();
  const [isOpenConfirmDialog, toggleIsOpenConfirmDialog] = useToggle();
  const [showSendInvoiceModal, toggleSendInvoiceModal]   = useToggle();

  const filtersData = useMemo<ISearchFilter[]>(
    () => [{
      propertyName : 'CUID',
      operator     : 'eq',
      value        : cuid,
    }],
    [cuid],
  );

  const [payload, setPayload] = useState<ISearchParams>({
    offset  : 0,
    limit   : ROWS_PER_PAGE,
    orderBy : 'InvoiceNumber desc',
    filters : filtersData,
  });

  const isValidCuid = useMemo<boolean>(
    () => !!cuid && cuid.length >= 5,
    [cuid],
  );

  // #region API INTEGRATION
  const { refetch, isLoading } = useFetchUsageAuctions(
    { invoiceId, cuid },
    {
      enabled   : false,
      onSuccess : (data: IUsageAuctions[]) => setUsageAuctions(data),
    },
  );

  const {
    data      : invoiceEmails = [],
    isLoading : isLoadingEmails,
  } = useFetchInvoiceEmails(cuid, {
    enabled: !!cuid,
  });

  const { data: cuInfo } = useFetchAccountById(cuid, { enabled: isValidCuid });

  const { mutateAsync: sendInvoices, isLoading: isInvoiceSending } = useSendInvoiceEmails({
    // eslint-disable-next-line no-console
    onSuccess: () => toggleSendInvoiceModal(),
  });

  const {
    data        : invoicesData,
    isLoading   : isLoadingInvoices,
  } = useFetchInvoices(payload);

  const { isLoading: isLoadingCurInvoice } = useFetchInvoiceById(
    {
      id: invoiceId,
      cuid,
    },
    {
      enabled   : !!invoiceId,
      onSuccess : (data) => setCurrentInvoice(data),
    },
  );

  const { mutateAsync: downloadPdfInvoice } = useDownloadPdfInvoice();

  const { mutateAsync: updateInvoice } = useUpdateInvoice({
    onSuccess: (data) => setCurrentInvoice(data),
  });
  // #endregion

  const invoices = useMemo<ISearchInvoice[]>(
    () => (invoicesData ? parseRecords<ISearchInvoice>(invoicesData) : []),
    [invoicesData],
  );

  const totalInvoicesCount = useMemo<number>(
    () => invoicesData?.totalRecordsFound ?? 0,
    [invoicesData],
  );

  const invoicesEmailOptions = useMemo(
    () => (
      invoiceEmails.map((email, index) => ({
        label : `${email}`,
        name  : `${index}`,
        value : true,
      }))),
    [invoiceEmails],
  );

  const onDownloadInvoice = useCallback(
    async (): Promise<void> => {
      const invoicePeriod = getFormattedDate(currentInvoice?.invoiceDate, 'MMMM-YYYY');
      const fileName      = `Invoice-${invoicePeriod}`;

      await downloadPdfInvoice({ id: invoiceId, cuid, fileName });
    },
    [currentInvoice, downloadPdfInvoice, invoiceId, cuid],
  );

  const onChangePage = useCallback(
    async (pageNumber: number): Promise<void> => {
      setCurrentPage(pageNumber);
      setPayload((prevValues) => ({
        ...prevValues,
        offset  : pageNumber * ROWS_PER_PAGE,
        limit   : ROWS_PER_PAGE,
        filters : filtersData,
      }));
    },
    [filtersData],
  );

  const onSaveInvoice = useCallback(
    async (values: TInvoiceValues): Promise<void> => {
      if (!currentInvoice) {
        return;
      }

      await updateInvoice({
        ...currentInvoice,
        ...values,
      });
    },
    [currentInvoice, updateInvoice],
  );

  const openNadaUsageModal = useCallback(
    async (): Promise<void> => {
      await refetch({ throwOnError: true });
      toggleIsNadaModalOpen();
    },
    [refetch, toggleIsNadaModalOpen],
  );

  const onViewAuctionsClick = useCallback(
    (): void => replace(`${EAppPage.Auctions}/${EAccountPage.NadaAuctions}/${cuid}/${invoiceId}`),
    [cuid, invoiceId, replace],
  );

  const handleOnConfirmResetFee = useCallback(
    (): void => {
      if (!formikRef.current) return;

      const { values, setValues } = formikRef.current;

      const {
        invoiceFees : invoiceFeesValues = [],
        status      : statusValue,
      } = values;

      const invoiceFees: IInvoiceFee[] = invoiceFeesValues.map((fee) => (fee.type === resetFeeType
        ? {
          ...fee,
          value  : 0,
          status : EPaymentStatus.Unpaid,
        }
        : fee));

      const status = getInvoiceStatus({ ...values, invoiceFees });

      setValues({
        ...values,
        invoiceFees,
        ...(status !== statusValue && { status }),
      });

      setResetFeeType(EFeeType.Unknown);
      toggleIsOpenConfirmDialog();
    },
    [resetFeeType, toggleIsOpenConfirmDialog],
  );

  const onDeleteSoldLot = useCallback(
    (soldLotId: string): void => {
      setDeletedSoldLotId(soldLotId);
      toggleIsOpenConfirmDialog();
    },
    [toggleIsOpenConfirmDialog],
  );

  const onCancelDeletion = useCallback(
    (): void => {
      setDeletedSoldLotId(null);
      setResetFeeType(EFeeType.Unknown);
      toggleIsOpenConfirmDialog();
    },
    [toggleIsOpenConfirmDialog],
  );

  const handleOnConfirmDeleteSoldLot = useCallback(
    (): void => {
      if (!formikRef.current) return;

      const { values, setValues } = formikRef.current;

      const {
        soldLots : soldLotsValues = [],
        status   : statusValue,
      } = values;

      const soldLots: ISoldLot[] = soldLotsValues.filter(
        (soldLot) => soldLot.id !== deletedSoldLotId,
      );

      const status = getInvoiceStatus({ ...values, soldLots });

      setValues({
        ...values,
        soldLots,
        ...(status !== statusValue && { status }),
      });

      setDeletedSoldLotId(null);
      toggleIsOpenConfirmDialog();
    },
    [toggleIsOpenConfirmDialog, deletedSoldLotId],
  );

  const onResetFee = useCallback(
    (feeType: EFeeType): void => {
      setResetFeeType(feeType);
      toggleIsOpenConfirmDialog();
    },
    [toggleIsOpenConfirmDialog],
  );

  const onChangeInvoiceStatus = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (!formikRef.current) return;

      const { values, setValues } = formikRef.current;

      const { name, value } = event.target;

      let formValues: Partial<TInvoiceValues> = {
        [name]: value,
      };

      if (+value === EInvoiceStatus.Paid) {
        const {
          soldLots    : soldLotsValues = [],
          invoiceFees : invoiceFeesValues = [],
        } = values;

        const soldLots: ISoldLot[] = soldLotsValues.map((lot) => ({
          ...lot,
          status: EPaymentStatus.Paid,
        }));

        const invoiceFees: IInvoiceFee[] = invoiceFeesValues.map((fee) => ({
          ...fee,
          status: EPaymentStatus.Paid,
        }));

        formValues = { ...formValues, soldLots, invoiceFees };
      }

      setValues({ ...values, ...formValues });
    },
    [formikRef],
  );

  const onChangeInvoiceFeeStatus = useCallback(
    (
      event   : React.ChangeEvent<HTMLInputElement>,
      feeType : EFeeType,
    ): void => {
      if (!formikRef.current) return;

      const { value = EPaymentStatus.Unpaid as EPaymentStatus } = event.target;

      const { values, setValues } = formikRef.current;

      const {
        invoiceFees : invoiceFeesValues = [],
        status      : statusValue,
      } = values;

      const invoiceFees: IInvoiceFee[] = invoiceFeesValues.map((fee) => (fee.type === feeType
        ? { ...fee, status: value as EPaymentStatus }
        : fee));

      const status = getInvoiceStatus({ ...values, invoiceFees });

      setValues({
        ...values,
        invoiceFees,
        ...(status !== statusValue && { status }),
      });
    },
    [formikRef],
  );

  const handleSendInvoice = useCallback(
    async (data: IInvoicesEmailsPayload) => {
      const emails = data.additionalEmail
        ? data.additionalEmail.split(' ').concat(data.invoiceEmails)
        : data.invoiceEmails;

      await sendInvoices({
        emails,
        cuid      : cuInfo?.cuData.cuid || '',
        invoiceId : invoiceId || '',
      });
    },
    [cuInfo, invoiceId, sendInvoices],
  );

  const onChangeSoldLotStatus = useCallback(
    (
      event : React.ChangeEvent<HTMLInputElement>,
      lotId : string,
    ): void => {
      if (!formikRef.current) return;

      const { value = EPaymentStatus.Unpaid as EPaymentStatus } = event.target;

      const { values, setValues } = formikRef.current;

      const {
        soldLots : soldLotsValues = [],
        status   : statusValue,
      } = values;

      const soldLots: ISoldLot[] = soldLotsValues.map((lot) => (lot.id === lotId
        ? { ...lot, status: value as EPaymentStatus }
        : lot));

      const status = getInvoiceStatus({ ...values, soldLots });

      setValues({
        ...values,
        soldLots,
        ...(status !== statusValue && { status }),
      });
    },
    [formikRef],
  );

  useEffect(
    () => {
      if (!invoiceId) {
        setPayload({
          offset  : 0,
          limit   : ROWS_PER_PAGE,
          orderBy : 'InvoiceNumber desc',
          filters : filtersData,
        });
      }
    },
    [filtersData, invoiceId],
  );

  return (
    <>
      {!invoiceId && (
        <InvoiceListPage
          push               = {push}
          invoices           = {invoices}
          accountData        = {currentAccount}
          currentPage        = {currentPage}
          rowsPerPage        = {ROWS_PER_PAGE}
          totalInvoicesCount = {totalInvoicesCount}
          isLoading          = {isLoadingInvoices}
          handleChangePage   = {onChangePage}
          cuid               = {cuid}
        />
      )}

      {invoiceId && currentInvoice && (
        <InvoiceDetailsPage
          isDownloading            = {isLoadingCurInvoice}
          invoiceData              = {currentInvoice}
          formikRef                = {formikRef}
          cuInfo                   = {cuInfo?.cuData || null}
          invoicesEmailOptions     = {invoicesEmailOptions}
          isLoadingEmails          = {isLoadingEmails}
          isInvoiceSending         = {isInvoiceSending}
          showSendInvoiceModal     = {showSendInvoiceModal}
          push                     = {push}
          toggleSendInvoiceModal   = {toggleSendInvoiceModal}
          handleSaveChanges        = {onSaveInvoice}
          handleDownloadInvoice    = {onDownloadInvoice}
          openNadaUsageModal       = {openNadaUsageModal}
          handleDeleteSoldLot      = {onDeleteSoldLot}
          handleResetFee           = {onResetFee}
          handleSendInvoice        = {handleSendInvoice}
          onChangeInvoiceStatus    = {onChangeInvoiceStatus}
          onChangeInvoiceFeeStatus = {onChangeInvoiceFeeStatus}
          onChangeSoldLotStatus    = {onChangeSoldLotStatus}
        />
      )}

      <NadaUsageModal
        open                = {isNadaModalOpen}
        isLoading           = {isLoading}
        usageAuctions       = {usageAuctions}
        onViewAuctionsClick = {onViewAuctionsClick}
        onClose             = {toggleIsNadaModalOpen}
      />

      <ConfirmDialog
        open      = {isOpenConfirmDialog}
        onCancel  = {onCancelDeletion}
        onConfirm = {
          deletedSoldLotId === null
            ? handleOnConfirmResetFee
            : handleOnConfirmDeleteSoldLot
        }
      />
    </>
  );
};
